import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { RetailCrmService } from '../../customer/clients/retail-crm/retail-crm.service';
import { CustomerCreationResponse } from '../../customer/model/create-customer.model';
import { GcdmSyncService } from '../../customer/services/gcdm-sync.service';
import { CustomerCreationOutcome } from '../model/customer-creation-outcome';
import { CustomerForCreation } from '../model/customer-for-creation';

@Injectable({
  providedIn: 'root',
})
export class CustomerCreationService {
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly gcdmSyncService = inject(GcdmSyncService);
  readonly logger = inject(NGXLogger);

  /**
   * Creates a new customer, either in the NSC Database and Retail CRM (case A1) or in
   * Retail CRM only (case A2).
   *
   * For A1 the NSC Database is written first and Retail CRM imports the resulting GCID, so the
   * GCID is known synchronously.
   */
  async createCustomer({ customer, syncToNscDatabase }: CustomerForCreation): Promise<CustomerCreationOutcome> {
    if (syncToNscDatabase) {
      return this.createSyncedCustomer(customer);
    }

    const response: CustomerCreationResponse = await this.retailCrmService.createAccount(customer);

    if (!response.accountNumber) {
      throw new Error('#CDFTF0273 Retail CRM did not return an account number for the created customer.');
    }

    return { status: 'CREATED', accountNumber: response.accountNumber };
  }

  private async createSyncedCustomer(customer: CustomerForCreation['customer']): Promise<CustomerCreationOutcome> {
    const result = await this.gcdmSyncService.createSyncedCustomer(customer);

    if (result.status === 'RETAIL_LINK_FAILED') {
      return { status: 'NSC_LINK_FAILED', gcid: result.gcid };
    }

    if (!result.accountNumber) {
      // The import answered 2xx without an account number: the customer exists in the NSC
      // Database but CDF cannot open its Retail CRM profile, which is the same situation.
      this.logger.warn(`#CDFTF0274 Retail CRM imported GCID ${result.gcid} but returned no account number.`);
      return { status: 'NSC_LINK_FAILED', gcid: result.gcid };
    }

    return { status: 'CREATED', accountNumber: result.accountNumber, gcid: result.gcid };
  }
}
